<script setup lang="ts">
import { ADialogTeleporter } from '@atabix/vue-ui'
import { computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

import { useAuthDriver } from '@/auth'
import AuthLayout from '@/components/AuthLayout.vue'
import ErrorLayout from '@/components/ErrorLayout.vue'
import MobileLayout from '@/components/Lunch/MobileLayout.vue'
import { useAppStore } from '@/stores/app'
import { useAuthStore } from '@/stores/auth'
import { useIdleStore } from '@/stores/idle'
import SplashView from '@/views/SplashView.vue'

const { t } = useI18n({
  messages: {
    en: {
      'idle description': 'You have been idle for a while.',
      'unexpected error occurred': 'An unexpected error occurred.',
      'unknown environment': 'This environment is not configured.',
      'maintenance description': 'The platform is currently in maintenance.',
      'offline description': 'You are currently offline. Check your internet connection and try again.',
      error: 'Error',
      unknown: 'Unknown',
      maintenance: 'Maintenance',
      offline: 'Offline',
    },
    nl: {
      'idle description': 'Je bent een tijdje inactief geweest.',
      'unexpected error occurred': 'Er is een onverwachte fout opgetreden.',
      'unknown environment': 'Deze omgeving is niet geconfigureerd.',
      'maintenance description': 'Het platform is momenteel in onderhoud.',
      'offline description': 'Je bent momenteel offline. Controleer je internetverbinding en probeer het opnieuw.',
      error: 'Foutmelding',
      unknown: 'Onbekend',
      maintenance: 'Onderhoud',
      offline: 'Offline',
    },
  },
})

const auth = useAuthStore()
const idle = useIdleStore()
const app = useAppStore()
const route = useRoute()

const isAdminRoute = computed((): boolean =>
  route.matched.some((record): boolean => record.path === '/admin'),
)

watch(
  (): boolean => auth.isAuthenticated,
  (isAuthenticated): void => {
    if (!isAuthenticated) {
      app.setError(undefined)
    }
  },
)

watch(
  (): boolean => idle.isIdle,
  (isIdle): void => {
    if (!auth.isAuthenticated) return
    if (!isIdle) return

    // Prompt the user before acting -- idle.isIdle already accounts for real elapsed
    // time (not throttled background timers) and is synced across tabs via the
    // BroadcastChannel, so this only fires when the user is genuinely away everywhere,
    // not merely backgrounded in one tab while active in another. Log out only if the
    // prompt is ignored until it expires.
    idle.prompt(t('idle description'), (): void => {
      useAuthDriver().logout()
    })
  },
)
</script>

<template>
  <main class="relative flex min-h-screen flex-col bg-background-tint">
    <template v-if="app.isReady">
      <AuthLayout v-if="isAdminRoute" />
      <MobileLayout v-else />
    </template>

    <!-- INITIALIZED is still a loading state, not a ready one, so it shares the splash with
    INITIALIZING. The trailing v-else is a catch-all so a future ApplicationState value can
    never fall through to a blank <main> -- it renders the splash rather than nothing. -->
    <SplashView v-else-if="app.isInitializing || app.isInitialized" />
    <ErrorLayout v-else-if="app.isFatal" :description="app.errorMessage || t('unexpected error occurred')" :title="t('error')" />
    <ErrorLayout v-else-if="app.isUnknown" :description="t('unknown environment')" :title="t('unknown')" />
    <ErrorLayout v-else-if="app.isMaintenance" :description="t('maintenance description')" :title="t('maintenance')" />
    <ErrorLayout v-else-if="app.isOffline" :description="t('offline description')" :title="t('offline')" />
    <SplashView v-else />
  </main>

  <ADialogTeleporter />
</template>

<style scoped></style>
